<template>
	<nav v-if="totalCount > pageSize" class="pagination">
		<section class="page-links">
			<div class="page-links__group">
				<component
					v-if="showFirstJump"
					:is="currentPage > 1 ? NuxtLink : 'span'"
					:to="{ query: { [queryParam]: getSanitisedPage(1) } }"
					:class="{ 'page-links__first link-item': true, 'link-item--disabled': currentPage <= 1 }"
				>
					<FontAwesomeIcon :icon="faChevronLeft" />
					<FontAwesomeIcon :icon="faChevronLeft" />
				</component>

				<component
					:is="currentPage > 1 ? NuxtLink : 'span'"
					:class="{ 'link-item': true, 'link-item--disabled': currentPage <= 1 }"
					:to="{ query: { [queryParam]: getSanitisedPage(Math.max(1, currentPage - 1)) } }"
				>
					<FontAwesomeIcon :icon="faChevronLeft" />
				</component>
			</div>

			<div class="page-links__group page-links__numbers">
				<NuxtLink
					v-if="currentPage > 2 && currentPage === lastPage"
					:to="{ query: { [queryParam]: getSanitisedPage(currentPage - 2) } }"
					class="link-item"
				>
					{{ currentPage - 2 }}
				</NuxtLink>

				<NuxtLink
					v-if="currentPage > 1"
					:to="{ query: { [queryParam]: getSanitisedPage(currentPage - 1) } }"
					custom
					v-slot="{ href, navigate }"
				>
					<a rel="prev" class="link-item" :href="href" @click="navigate">
						{{ currentPage - 1 }}
					</a>
				</NuxtLink>

				<NuxtLink :to="{ query: { [queryParam]: getSanitisedPage(currentPage) } }" class="link-item active">
					{{ currentPage }}
				</NuxtLink>

				<NuxtLink
					v-if="currentPage < lastPage"
					:to="{ query: { [queryParam]: getSanitisedPage(currentPage + 1) } }"
					custom
					v-slot="{ href, navigate }"
				>
					<a rel="next" :href="href" @click="navigate" class="link-item">
						{{ currentPage + 1 }}
					</a>
				</NuxtLink>

				<NuxtLink
					v-if="currentPage === 1 && currentPage + 2 <= lastPage"
					:to="{ query: { [queryParam]: getSanitisedPage(currentPage + 2) } }"
					class="link-item"
				>
					{{ currentPage + 2 }}
				</NuxtLink>
			</div>

			<div class="page-links__group page-links__group--right">
				<component
					:to="{ query: { [queryParam]: getSanitisedPage(Math.min(lastPage, currentPage + 1)) } }"
					:is="currentPage !== lastPage ? NuxtLink : 'span'"
					:class="{ 'link-item': true, 'link-item--disabled': currentPage === lastPage }"
				>
					<FontAwesomeIcon :icon="faChevronRight" />
				</component>

				<component
					v-if="showLastJump"
					:to="{ query: { [queryParam]: getSanitisedPage(lastPage) } }"
					:is="currentPage !== lastPage ? NuxtLink : 'span'"
					:class="{ 'page-links__last link-item': true, 'link-item--disabled': currentPage === lastPage }"
				>
					<FontAwesomeIcon :icon="faChevronRight" />
					<FontAwesomeIcon :icon="faChevronRight" />
				</component>
			</div>
		</section>

		<p v-if="showStatus" class="status">
			{{ (currentPage - 1) * pageSize + 1 }}-{{ Math.min(totalCount, (currentPage - 1) * pageSize + pageSize) }} /
			{{ totalCount }}
		</p>
	</nav>
</template>

<script setup lang="ts">
/*
Pagination in the query is 0-based indexed but in the interface it
starts at 1 to be more intuitive for users. That's why there's so
many ±1 here :|
*/
import { NuxtLink } from '#components';
import { usePagination } from '@/helpers/composables/usePagination';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';

const props = withDefaults(
	defineProps<{
		queryParam?: string;
		showStatus?: boolean;
		showFirstJump?: boolean;
		showLastJump?: boolean;
		pageSize: number;
		totalCount: number;
		lastPage: number;
	}>(),
	{
		queryParam: 'page',
		showStatus: true,
		showFirstJump: true,
		showLastJump: true,
	}
);

// we don't want to display `?page=1`
function getSanitisedPage(page: number): number | undefined {
	return page > 1 ? page : undefined;
}

const { currentPage } = usePagination({ lastPage: props.lastPage });
</script>

<style scoped lang="scss">
@import '~@/styles/sports.scss';

.pagination {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 8px;

	.page-links {
		width: 100%;
		display: flex;
		justify-content: space-between;
		align-items: center;

		--link-size: 32px;

		.link-item {
			display: flex;
			justify-content: center;
			place-items: center;

			min-height: var(--link-size);
			height: var(--link-size);
			min-width: var(--link-size);
			width: var(--link-size);
			margin: 0;
			padding: 0;
			border-radius: 8px;
			color: $color-primary-light;
			border: 1px solid $color-primary-light;

			&--disabled {
				opacity: 0.3;
			}
		}

		&__group {
			display: flex;
			gap: 16px;
			align-items: baseline;
			flex-basis: 80px;

			&--right {
				justify-content: flex-end;
			}
		}

		&__numbers {
			.link-item.active {
				color: $color-neutral;
				background-color: $color-primary-light;
			}
		}
	}

	.status {
		margin: 0;
		font-size: 14px;
		color: $color-primary-light;
	}
}
</style>
